import React from 'react';
import { OnChangeValue } from 'react-select';
import { Divider, Switch } from 'src/components/atoms';
import {
  DefaultOption,
  FormInput,
  InformationBanner,
  ParameterList,
  RoutingParameter,
} from 'src/components/molecules';
import { ParameterType } from 'src/components/molecules/Parameter/helpers';
import { GroupType, RoutingRuleType } from 'src/graphql';
import { FormInputVariant, FormState, RoutingDataSource } from 'src/types';
import { useTheme } from 'styled-components';
import {
  Attributes,
  RoutingVariant,
  RoutingVariantType,
} from '../RoutingRuleAndPriority';
import {
  DividerContainer,
  RoutingRuleBodyStyled,
  RoutingRuleButtonGroup,
  RoutingRuleDataSource,
  RoutingRuleDescriptionText,
  RoutingRuleGroup,
  RoutingRuleGroupAssignment,
  RoutingRuleHeaderText,
  RoutingRuleParameterJoiner,
  RoutingRuleParameterList,
  SpeakUpDataSourceDescription,
} from './RoutingRuleBody.styles';

interface Props {
  rule: RoutingRuleType;
  selectedDataSource: RoutingDataSource;
  setSelectedDataSource: (selectedDataSource: RoutingDataSource) => void;
  groups: GroupType[];
  selectedGroup?: GroupType;
  updateRule: (value: OnChangeValue<DefaultOption, boolean>) => void;
  groupDropdownState: FormState;
  parameterList: ParameterList;
  parameterOptions: DefaultOption[];
  attributes: Attributes;
  setParameterList: (
    parameterList: React.SetStateAction<ParameterList>,
  ) => void;
  groupAssignment: boolean;
  setGroupAssignment: (value: boolean) => void;
  isCOIEnabled: boolean;
  variant?: RoutingVariantType;
  tabIndex?: number;
}

const DATA_SOURCES = [RoutingDataSource.OFFENDER, RoutingDataSource.REPORTER];
const COI_DATA_SOURCES = [RoutingDataSource.REPORTER];
const speakUpDataSourceDescription =
  'Understanding the source of data (named offender or reporter data) is crucial for ensuring accuracy, credibility, and legal implications in decision-making.';
const legacyDataSourceDescription = `This is critical for rule parameters to be searched accurately.
            Determine if 'Site, Department, Country, Permission and
            Relationship to Organization Categories' are based on data
            related to the Named Offender or the Reporter. For all other
            parameters, the choice of data source does not matter.`;
export const RoutingRuleBody = ({
  rule,
  selectedDataSource,
  setSelectedDataSource,
  groups,
  selectedGroup,
  updateRule,
  groupDropdownState,
  parameterList,
  parameterOptions,
  attributes,
  setParameterList,
  groupAssignment,
  setGroupAssignment,
  isCOIEnabled,
  variant,
  tabIndex,
}: Props) => {
  const dataSources =
    variant === RoutingVariant.COI ? COI_DATA_SOURCES : DATA_SOURCES;
  const theme = useTheme();
  const isVaultVariant = theme.isVaultVariant;
  const dividerColor = isVaultVariant
    ? theme.newPalette.neutral.N300
    : theme.atlas.core.color.gray[200];
  const isSpeakUpVariant = variant === RoutingVariant.SPEAK_UP;
  const isCoiVariant = variant === RoutingVariant.COI;

  const groupHelpText = isCoiVariant
    ? 'Ensure the selected group has active Compliance Reviewers. Please choose a different group for the Rule to work effectively.'
    : 'Ensure the selected group has active Case Managers. Please choose a different group for the Rule to work effectively.';
  return (
    <>
      <RoutingRuleBodyStyled
        hasPaddingBottom={groupDropdownState !== FormState.ERROR}
      >
        <RoutingRuleDataSource $speakUp={isSpeakUpVariant} $coi={isCoiVariant}>
          <RoutingRuleHeaderText>Select data source</RoutingRuleHeaderText>
          <RoutingRuleButtonGroup
            options={dataSources}
            selectedOption={selectedDataSource}
            size="medium"
            setSelectedOption={setSelectedDataSource}
            buttonWidth="100%"
            disabled={!rule.disabled}
            buttonGroupOptionTestId="data-source-button-group"
            id={`${rule.id}-data-source`}
            tabIndex={tabIndex}
            $speakUp={isSpeakUpVariant}
          />
          <RoutingRuleDescriptionText>
            <SpeakUpDataSourceDescription data-testid="data-source-description">
              {isSpeakUpVariant && speakUpDataSourceDescription}
              {variant === undefined && legacyDataSourceDescription}
            </SpeakUpDataSourceDescription>
          </RoutingRuleDescriptionText>
        </RoutingRuleDataSource>
        {variant === RoutingVariant.COI &&
          selectedGroup?.hasNonCOIReviewerMembers && (
            <InformationBanner
              data-testid="coi-group-non-reviewer-warning"
              state={FormState.WARNING}
              title="Not all group members have access"
              description="Some members of the selected group do not have the COI Reviewer role. Only users with the required permissions will be able to view and review disclosures."
            />
          )}
        <RoutingRuleGroup>
          <RoutingRuleHeaderText>Route to</RoutingRuleHeaderText>
          <FormInput
            variant={FormInputVariant.DROPDOWN}
            manualMenuOpen
            options={groups.map((group) => ({
              label: group.name ?? '',
              value: group.id,
            }))}
            value={
              rule.destinationGroup
                ? {
                    label: selectedGroup?.name ?? '',
                    value: selectedGroup?.id ?? '',
                  }
                : undefined
            }
            placeholder="Example: Group 1"
            onChange={updateRule}
            state={groupDropdownState}
            helpText={groupHelpText}
            disabled={!rule.disabled}
            data-testid="destination-group-select"
            tabIndex={tabIndex}
          />
          {isCOIEnabled ? (
            <RoutingRuleGroupAssignment>
              <Switch
                checked={groupAssignment}
                label="Assign to group"
                onClick={setGroupAssignment}
                disabled={!rule.disabled}
                data-testid="group-assignment-toggle"
              />
              <RoutingRuleDescriptionText>
                Assign the whole reviewer group to the case. A random member
                becomes the case lead and the remaining members are added as
                case team members with edit permissions.
              </RoutingRuleDescriptionText>
            </RoutingRuleGroupAssignment>
          ) : null}
        </RoutingRuleGroup>
      </RoutingRuleBodyStyled>
      <DividerContainer>
        <Divider variant="horizontal" color={dividerColor} />
      </DividerContainer>
      <RoutingRuleParameterList>
        {Object.keys(parameterList).map((parameter, index, arr) => (
          <RoutingParameter
            key={parameter}
            parameter={parameter as ParameterType}
            isFirstParameter={index === 0}
            isLastParameter={index === arr.length - 1}
            parameterOptions={parameterOptions}
            parameterList={parameterList}
            selectedDataSource={selectedDataSource}
            setParameterList={setParameterList}
            attributes={attributes}
            showOperators
            disabled={!rule.disabled}
            data-testid={`routing-parameter-${index}`}
            tabIndex={tabIndex}
          />
        ))}
        {Object.keys(parameterList).length > 1 ? (
          <RoutingRuleParameterJoiner>
            <Divider variant="vertical" height="100%" color={dividerColor} />
          </RoutingRuleParameterJoiner>
        ) : null}
      </RoutingRuleParameterList>
      <DividerContainer>
        <Divider variant="horizontal" color={dividerColor} />
      </DividerContainer>
    </>
  );
};
